import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCapture({ onSubmit }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      toast.success("Welcome! Let's check your hearing.");
      onSubmit({
        ...formData,
        id: response.data.id,
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#22D3EE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-[#22D3EE]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
            />
          </svg>
        </div>
        <h1 className="text-3xl sm:text-4xl font-light text-[#F8FAFC] mb-3 font-['Outfit'] tracking-tight">
          Free Online Hearing Test
        </h1>
        <p className="text-[#94A3B8] text-base sm:text-lg">
          Get your results in under 5 minutes. Start by entering your details below.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label
            htmlFor="name"
            className="text-sm font-medium text-[#94A3B8] mb-2 block"
          >
            Full Name <span className="text-[#F87171]">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter your name"
            value={formData.name}
            onChange={handleChange}
            className={`bg-[#0A0F1C] border-[#334155] text-[#F8FAFC] placeholder-[#64748B] h-12 focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent ${
              errors.name ? "border-[#F87171]" : ""
            }`}
            data-testid="lead-name-input"
          />
          {errors.name && (
            <p className="text-[#F87171] text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <Label
            htmlFor="email"
            className="text-sm font-medium text-[#94A3B8] mb-2 block"
          >
            Email Address <span className="text-[#F87171]">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            className={`bg-[#0A0F1C] border-[#334155] text-[#F8FAFC] placeholder-[#64748B] h-12 focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent ${
              errors.email ? "border-[#F87171]" : ""
            }`}
            data-testid="lead-email-input"
          />
          {errors.email && (
            <p className="text-[#F87171] text-sm mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <Label
            htmlFor="phone"
            className="text-sm font-medium text-[#94A3B8] mb-2 block"
          >
            Phone Number <span className="text-[#64748B]">(optional)</span>
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData.phone}
            onChange={handleChange}
            className="bg-[#0A0F1C] border-[#334155] text-[#F8FAFC] placeholder-[#64748B] h-12 focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent"
            data-testid="lead-phone-input"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 px-6 bg-[#22D3EE] text-[#0A0F1C] font-semibold rounded-lg hover:bg-[#67E8F9] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
          data-testid="lead-submit-btn"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Starting...
            </>
          ) : (
            <>
              Start Hearing Test
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </>
          )}
        </button>
      </form>

      <p className="text-center text-[#64748B] text-xs mt-6">
        By continuing, you agree to receive your test results via email.
      </p>
    </div>
  );
}
